/**
 * React-komponent för auto-update-banner.
 *
 * Två mekanismer:
 * 1. Service worker "waiting"-läge (klassisk SW-update).
 * 2. Versions-pollning: hämtar /index.html (no-store) och jämför JS-bundle-
 *    hashen (main.<hash>.js) med den som körs. Fungerar även på iOS-PWA där
 *    SW-updates är opålitliga. Kollar vid start, när appen får fokus igen
 *    och var 30:e minut.
 *
 * Klick på "Uppdatera" → rensa Cache Storage + SKIP_WAITING + hård reload.
 * NYTT: Vid appstart/återöppning uppdateras appen TYST automatiskt (ingen
 * banner). Bannern visas bara som fallback: mitt i en session (30-min-poll)
 * eller om loop-skyddet (max 1 auto-reload / 2 min) slagit till.
 * Mountas EN gång i App.jsx på root-nivå.
 */
import { useEffect, useState } from "react";

function currentBundleHash() {
  const script = Array.from(document.querySelectorAll("script[src]"))
    .map((s) => s.getAttribute("src") || "")
    .find((src) => /main\.[a-f0-9]+\.js/.test(src));
  const m = (script || "").match(/main\.([a-f0-9]+)\.js/);
  return m ? m[1] : null;
}

async function serverBundleHash() {
  try {
    const res = await fetch("/index.html", { cache: "no-store" });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(/main\.([a-f0-9]+)\.js/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

export function UpdateBanner() {
  const [waitingReg, setWaitingReg] = useState(null);
  const [newVersion, setNewVersion] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    let cancelled = false;

    // Tyst auto-uppdatering med loop-skydd: max 2 auto-reloads per 10 min.
    // Om CDN:et fortfarande serverar gammal bundle efter 2 försök → ge upp
    // tyst och försök igen vid nästa appstart (ingen banner-loop, ingen vit skärm).
    const AUTO_KEY = "grind_auto_update_state";
    const getAutoState = () => {
      try { return JSON.parse(localStorage.getItem(AUTO_KEY)) || { ts: 0, count: 0 }; }
      catch { return { ts: 0, count: 0 }; }
    };
    const canAutoUpdate = () => {
      const s = getAutoState();
      if (Date.now() - s.ts > 10 * 60 * 1000) return true; // nytt fönster
      return s.count < 2;
    };
    const markAutoUpdate = () => {
      const s = getAutoState();
      const fresh = Date.now() - s.ts > 10 * 60 * 1000;
      localStorage.setItem(AUTO_KEY, JSON.stringify({
        ts: fresh ? Date.now() : s.ts,
        count: fresh ? 1 : (s.count || 0) + 1,
      }));
    };
    const clearAutoState = () => localStorage.removeItem(AUTO_KEY);

    const silentUpdate = async (reg) => {
      markAutoUpdate();
      // Bevara ev. väntande push-deep-link och följ med den efter omladdningen
      let target = window.location.pathname;
      try {
        const nav = await caches.open("pending-nav-v1");
        const r = await nav.match("/__pending_nav__");
        if (r) {
          const { path, ts } = await r.json();
          if (typeof path === "string" && Date.now() - (ts || 0) < 60 * 1000) {
            target = path;
            await nav.delete("/__pending_nav__");
          }
        }
      } catch { /* tyst */ }
      try {
        const keys = await caches.keys();
        // Rensa ALDRIG pending-nav/telemetri — de raderas annars innan appen
        // hunnit läsa dem (race med notisklickets deep-link)
        await Promise.all(
          keys
            .filter((k) => k !== "pending-nav-v1" && k !== "sw-telemetry-v1")
            .map((k) => caches.delete(k))
        );
      } catch { /* tyst */ }
      const sep = target.includes("?") ? "&" : "?";
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: "SKIP_WAITING" });
        // Fallback om controllerchange inte triggar reload inom 2s
        setTimeout(() => {
          window.location.href = `${target}${sep}v=${Date.now()}`;
        }, 2000);
        return;
      }
      window.location.href = `${target}${sep}v=${Date.now()}`;
    };

    const checkForWaiting = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg || cancelled) return;

        // Redan en waiting service worker → uppdatera tyst (eller banner som fallback)
        if (reg.waiting) {
          if (canAutoUpdate()) {
            silentUpdate(reg);
          } else {
            setWaitingReg(reg);
          }
        }

        // Lyssna på updatefound-event för framtida updates
        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed"
              && navigator.serviceWorker.controller
            ) {
              // Ny version installerad och redo
              setWaitingReg(reg);
            }
          });
        });
      } catch {
        // Service worker support saknas eller fel — tyst
      }
    };

    checkForWaiting();

    // Versions-pollning: jämför körande bundle-hash med serverns
    const localHash = currentBundleHash();
    const checkVersion = async (trigger) => {
      if (!localHash) return; // dev-läge (ingen hashad bundle)
      const remote = await serverBundleHash();
      if (!cancelled && remote && remote !== localHash) {
        // Vid appstart/återöppning: uppdatera tyst. Loop-skydd: efter 2
        // misslyckade försök (CDN serverar fortfarande gammal bundle) väntar
        // vi tyst till nästa fönster — INGEN banner-loop, ingen vit skärm.
        if (trigger !== "interval" && canAutoUpdate()) {
          const reg = await navigator.serviceWorker.getRegistration().catch(() => null);
          silentUpdate(reg);
          return;
        }
        if (trigger === "interval") setNewVersion(true);
        return;
      }
      if (!cancelled && remote && remote === localHash) clearAutoState();
      // Be även SW:n leta efter update (iOS gör det sällan själv)
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        reg?.update?.();
      } catch { /* tyst */ }
    };
    checkVersion("start");
    const onVisible = () => {
      if (document.visibilityState === "visible") checkVersion("resume");
    };
    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(() => checkVersion("interval"), 30 * 60 * 1000);

    // När controller byts (efter SKIP_WAITING + activate) → reload
    let reloaded = false;
    const onCtrlChange = () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onCtrlChange);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
      navigator.serviceWorker.removeEventListener("controllerchange", onCtrlChange);
    };
  }, []);

  const handleUpdate = async () => {
    // 1) Rensa Cache Storage så gammal shell/bundle inte serveras igen
    try {
      const keys = await caches.keys();
      await Promise.all(
        keys
          .filter((k) => k !== "pending-nav-v1" && k !== "sw-telemetry-v1")
          .map((k) => caches.delete(k))
      );
    } catch { /* tyst */ }
    // 2) Aktivera ev. väntande service worker
    if (waitingReg?.waiting) {
      waitingReg.waiting.postMessage({ type: "SKIP_WAITING" });
      return; // reload triggas via controllerchange
    }
    // 3) Hård reload med cache-bust
    window.location.href = `${window.location.pathname}?v=${Date.now()}`;
  };

  if ((!waitingReg && !newVersion) || dismissed) return null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[100] bg-amber-100 border-b border-amber-300 px-3 pb-2 flex items-center gap-2 shadow-sm"
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
      data-testid="update-banner"
    >
      <span className="text-amber-900 text-sm flex-1">
        ✨ <b>Ny version finns</b>
        <span className="hidden sm:inline">
          {" "}— tryck för att uppdatera
        </span>
      </span>
      <button
        onClick={handleUpdate}
        className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded font-medium"
        data-testid="update-banner-reload"
      >
        Uppdatera
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="text-amber-700 hover:text-amber-900 text-lg leading-none px-1"
        data-testid="update-banner-dismiss"
        aria-label="Stäng"
      >
        ×
      </button>
    </div>
  );
}
